import { apiRequest } from './client.js';

export interface ReviewRow {
  readonly requestLineId: string;
  readonly requestId: string;
  readonly requestNo: string;
  readonly lineNumber: number;
  readonly dealerCode: string;
  readonly companyName: string;
  readonly districtCode: string;
  readonly typeName: string;
  readonly itemName: string;
  readonly uomCode: string;
  readonly quantity: string;
  readonly revisedQuantity: string | null;
  /** Height and width of each piece for area work; 1 otherwise. */
  readonly dim1: string | null;
  readonly dim2: string | null;
  readonly lineStatusCode: string;
  /**
   * Why this row is not this person's to change, in the server's own words —
   * null when it is.
   *
   * Sent with the row rather than worked out here. Who holds a record is
   * decided by the tenant's approval flow, which this screen has no way of
   * knowing and no business guessing at.
   */
  readonly whyNotModifiable?: string | null;
  readonly forwardedAt: string | null;
  readonly brandingTypeId: string;
  readonly brandingItemId: string | null;
  /** Null on a request raised by someone who does not issue work orders. */
  readonly workOrderId: string | null;
  readonly workOrderLineId: string | null;
  readonly woNumber: string | null;
  readonly woLineNumber: number | null;
  /** What the attached work order line promised, and what is still free on it. */
  readonly woCommittedQuantity: string | null;
  readonly woBalanceQuantity: string | null;
  /** The vendor the attached work order commits; he is the only eligible one. */
  readonly woVendorId: string | null;
  readonly woVendorCode: string | null;
  readonly woVendorName: string | null;
  readonly vendorId: string | null;
  readonly vendorName: string | null;
  /** The approval the row waits on; decided on this page. */
  readonly workflowInstanceId: string | null;
  /** The level the row is pending at, for every reader (same page for every role). */
  readonly pendingAt: string | null;
  /** The approval is pending with this reader, who decides it on this page. */
  readonly canDecide: boolean;
}

export interface ImportOutcome {
  readonly updated: number;
  readonly rejected: ReadonlyArray<{ row: number; reason: string }>;
}

type Tok = string | null | undefined;
const BASE = '/v1/m17/branding-review';

export const loadReviewQueue = (token?: Tok) =>
  apiRequest<readonly ReviewRow[]>(`${BASE}/queue`, { token });

export const reviseLineQuantity = (lineId: string, quantity: number, token?: Tok) =>
  apiRequest<readonly ReviewRow[]>(
    `${BASE}/lines/${encodeURIComponent(lineId)}/revise`,
    { method: 'POST', token, body: { quantity } },
  );

export const importReviewCsv = (csv: string, token?: Tok) =>
  apiRequest<ImportOutcome>(`${BASE}/import`, { method: 'POST', token, body: { csv } });

/** Name the work order a line draws on, for a request that arrived without one. */
export const attachWorkOrderToLine = (
  lineId: string,
  workOrderLineId: string,
  token?: Tok,
) =>
  apiRequest<readonly ReviewRow[]>(
    `${BASE}/lines/${encodeURIComponent(lineId)}/work-order`,
    { method: 'POST', token, body: { workOrderLineId } },
  );

export const forwardLinesToVendor = (
  lineIds: readonly string[],
  token?: Tok,
  remarks?: string,
) =>
  apiRequest<{
    released: number;
    /** Complete rows waiting at the reader's level, approved to their next level. */
    advanced?: number;
    /**
     * Each row that could not go, and why — it is still here and editable.
     * The request number travels with it because the line number alone names
     * nothing: every request in this queue has a row 1.
     */
    held?: Array<{ requestNo: string; lineNumber: number; why: string }>;
  }>(
    `${BASE}/forward-to-vendor`,
    { method: 'POST', token, body: { lineIds, remarks } },
  );

/** Built here rather than fetched so the download carries the session token. */
export const exportUrl = (lineIds?: readonly string[]) =>
  `${BASE}/export${lineIds?.length ? `?lineIds=${lineIds.join(',')}` : ''}`;
